import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import RegisterStoreOwner from "@/pages/RegisterStoreOwner";
import AdminLayout from "@/pages/AdminLayout";
import Overview from "@/pages/admin/Overview";
import StoreOwners from "@/pages/admin/StoreOwners";
import Riders from "@/pages/admin/Riders";
import Packages from "@/pages/admin/Packages";
import Settings from "@/pages/admin/Settings";
import StoreLayout from "@/pages/StoreLayout";
import StoreOverview from "@/pages/store/Overview";
import Products from "@/pages/store/Products";
import AddProduct from "@/pages/store/AddProduct";
import QRGenerator from "@/pages/store/QRGenerator";
import QRCodes from "@/pages/store/QRCodes";
import CreatePackage from "@/pages/store/CreatePackage";
import MyPackages from "@/pages/store/MyPackages";
import StoreSettings from "@/pages/store/Settings";
import Dispatchers from "@/pages/store/Dispatchers";
import WhDispatchers from "@/pages/warehouse/Dispatchers";
import DispatcherActivity from "@/pages/store/DispatcherActivity";
import DispatcherLayout from "@/pages/DispatcherLayout";
import Scanner from "@/pages/dispatcher/Scanner";
import ForPickup from "@/pages/dispatcher/ForPickup";
import Processed from "@/pages/dispatcher/Processed";
import HistoryPage from "@/pages/dispatcher/History";
import RiderLayout from "@/pages/RiderLayout";
import RiderScanner from "@/pages/rider/Scanner";
import RiderActive from "@/pages/rider/Active";
import RiderHistory from "@/pages/rider/History";
import WarehouseLayout from "@/pages/WarehouseLayout";
import WarehouseOverview from "@/pages/warehouse/Overview";
import WarehouseIncoming from "@/pages/warehouse/Incoming";
import WarehouseScan from "@/pages/warehouse/ScanIntake";
import WarehouseReceived from "@/pages/warehouse/Received";
import PackageSorting from "@/pages/warehouse/PackageSorting";
import OutboundBatches from "@/pages/warehouse/OutboundBatches";
import CreateBatch from "@/pages/warehouse/CreateBatch";
import BatchDetail from "@/pages/warehouse/BatchDetail";
import WarehouseShipped from "@/pages/warehouse/ShippedOut";
import WarehouseReadyLastMile from "@/pages/warehouse/ReadyLastMile";
import WarehouseHistoryPage from "@/pages/warehouse/WarehouseHistory";
import WarehouseIssues from "@/pages/warehouse/Issues";
import WarehouseSettings from "@/pages/warehouse/Settings";
import AdminOutboundBatches from "@/pages/admin/OutboundBatches";
import AdminWarehouseDispatchers from "@/pages/admin/WarehouseDispatchers";
import AdminWarehouseIssues from "@/pages/admin/WarehouseIssues";
import AdminWarehouseHistory from "@/pages/admin/WarehouseHistory";
import Legal from "@/pages/Legal";
import TrackPackage from "@/pages/TrackPackage";
import RecipientView from "@/pages/RecipientView";
import InstallPrompt from "@/components/InstallPrompt";

function ProtectedRoute({ role, roles, children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-slate-950 text-slate-600 dark:text-slate-400">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  const allowed = roles ? roles.includes(user.role) : (role ? user.role === role : true);
  if (!allowed) {
    return <Navigate to={
      user.role === "STORE_DISPATCHER" ? "/dispatcher" :
      user.role === "STORE_OWNER" ? "/store" :
      user.role === "STORE_MANAGER" ? "/store" :
      user.role === "RIDER" ? "/rider" :
      user.role === "WAREHOUSE_ADMIN" ? "/warehouse" :
      user.role === "WAREHOUSE_MANAGER" ? "/warehouse" :
      user.role === "WAREHOUSE_PACKAGE_DISPATCHER" ? "/warehouse" : "/admin"
    } replace />;
  }
  return children;
}

export default function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register/store-owner" element={<RegisterStoreOwner />} />
            <Route path="/legal/:docSlug" element={<Legal />} />
            <Route path="/track/:code" element={<TrackPackage />} />
            <Route path="/recipient/:code" element={<RecipientView />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute roles={["SUPER_ADMIN", "SUB_ADMIN"]}>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Overview />} />
              <Route path="store-owners" element={<StoreOwners status="APPROVED,SUSPENDED,REJECTED" />} />
              <Route path="store-owners/pending" element={<StoreOwners status="PENDING" />} />
              <Route path="riders" element={<Riders status="APPROVED,SUSPENDED,REJECTED" />} />
              <Route path="riders/pending" element={<Riders status="PENDING" />} />
              <Route path="packages/waiting" element={<Packages status="WAITING_FOR_PICKUP" />} />
              <Route path="packages/picked-up" element={<Packages status="PICKED_UP_BY_RIDER" />} />
              <Route path="packages/dropped" element={<Packages status="DROPPED_AT_WAREHOUSE" />} />
              <Route path="packages/warehouse" element={<Packages status="RECEIVED_AT_WAREHOUSE" />} />
              <Route path="packages/sorting" element={<Packages status="SORTING_IN_WAREHOUSE,ADDED_TO_OUTBOUND_BATCH,OUTBOUND_BATCH_READY" />} />
              <Route path="packages/shipped" element={<Packages status="SHIPPED_OUT" />} />
              <Route path="packages/ready-last-mile" element={<Packages status="READY_FOR_LAST_MILE_PICKUP" />} />
              <Route path="packages/last-mile" element={<Packages status="OUT_FOR_LAST_MILE" />} />
              <Route path="packages/delivered" element={<Packages status="DELIVERED" />} />
              <Route path="outbound-batches" element={<AdminOutboundBatches />} />
              <Route path="warehouse-dispatchers" element={<AdminWarehouseDispatchers />} />
              <Route path="warehouse-issues" element={<AdminWarehouseIssues />} />
              <Route path="warehouse-history" element={<AdminWarehouseHistory />} />
              <Route path="settings" element={<Settings />} />
            </Route>
            <Route
              path="/store"
              element={
                <ProtectedRoute roles={["STORE_OWNER", "STORE_MANAGER"]}>
                  <StoreLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<StoreOverview />} />
              <Route path="products" element={<Products />} />
              <Route path="products/new" element={<AddProduct />} />
              <Route path="products/edit/:productId" element={<AddProduct />} />
              <Route path="qr-generator" element={<QRGenerator />} />
              <Route path="qr/blank" element={<QRCodes status="BLANK" />} />
              <Route path="qr/assigned" element={<QRCodes status="PRODUCT_ASSIGNED" />} />
              <Route path="create-package" element={<CreatePackage />} />
              <Route path="packages" element={<MyPackages />} />
              <Route path="dispatchers" element={<Dispatchers />} />
              <Route path="dispatcher-activity" element={<DispatcherActivity />} />
              <Route path="settings" element={<StoreSettings />} />
            </Route>
            <Route
              path="/dispatcher"
              element={
                <ProtectedRoute role="STORE_DISPATCHER">
                  <DispatcherLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Scanner />} />
              <Route path="for-pickup" element={<ForPickup />} />
              <Route path="processed" element={<Processed />} />
              <Route path="history" element={<HistoryPage />} />
            </Route>
            <Route
              path="/rider"
              element={
                <ProtectedRoute role="RIDER">
                  <RiderLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<RiderScanner />} />
              <Route path="active" element={<RiderActive />} />
              <Route path="history" element={<RiderHistory />} />
            </Route>
            <Route
              path="/warehouse"
              element={
                <ProtectedRoute roles={["WAREHOUSE_ADMIN", "WAREHOUSE_MANAGER", "WAREHOUSE_PACKAGE_DISPATCHER"]}>
                  <WarehouseLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<WarehouseOverview />} />
              <Route path="incoming" element={<WarehouseIncoming />} />
              <Route path="scan" element={<WarehouseScan />} />
              <Route path="received" element={<WarehouseReceived />} />
              <Route path="sorting" element={<PackageSorting />} />
              <Route path="batches" element={<OutboundBatches />} />
              <Route path="batches/new" element={<CreateBatch />} />
              <Route path="batches/:batchId" element={<BatchDetail />} />
              <Route path="shipped" element={<WarehouseShipped />} />
              <Route path="ready-last-mile" element={<WarehouseReadyLastMile />} />
              <Route path="dispatchers" element={<WhDispatchers />} />
              <Route path="history" element={<WarehouseHistoryPage />} />
              <Route path="issues" element={<WarehouseIssues />} />
              <Route path="settings" element={<WarehouseSettings />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster theme="system" position="top-right" richColors />
        <InstallPrompt />
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}
